/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AddTestCaseDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Checkbox,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import { componentStyle } from "../../../config/appTheme";
import CustomMultiSelectAutocompleteWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomMultiSelectAutocompleteWithApiV2";

function AddTestCaseDialog({ open, onClose, onSuccess }) {
  // Dispatch
  const dispatch = useDispatch();
  // Selectors
  const testCaseStatus = useSelector(
    (state) => state.settingsReducer?.metaData?.test_case_status,
  );
  // Test case id
  const [testCaseId, setTestCaseId] = useState("");
  const [testCaseIdError, setTestCaseIdError] = useState("");
  // Test case name
  const [testCaseName, setTestCaseName] = useState("");
  const [testCaseNameError, setTestCaseNameError] = useState("");
  // Description
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [environmentOptionsList, setEnvironmentOptionsList] = useState([]);
  const [environment, setEnvironment] = useState(null);
  const [isDestructible, setIsDestructible] = useState(false);
  const [needNewVehicle, setNeedNewVehicle] = useState(false);
  // EE Component
  const [selectedEEComponentIds, setSelectedEEComponentIds] = useState([]);
  // Software module
  const [selectedSoftwareModuleIds, setSelectedSoftwareModuleIds] = useState(
    [],
  );
  // Vehicle system
  const [selectedVehicleSystemIds, setSelectedVehicleSystemIds] = useState([]);
  // Vehicle component
  const [selectedVehicleComponentIds, setSelectedVehicleComponentIds] =
    useState([]);
  // Selected component
  const COMPONENT = { EE: "ee", SWM: "swm", VS: "vs", VC: "vc" };
  const [selectedComponent, setSelectedComponent] = useState(COMPONENT.VS);
  // Status
  const [statusOptionsList, setStatusOptionsList] = useState([]);
  const [status, setStatus] = useState(null);
  // Pre-conditions
  const createUid = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const EMPTY_PRE_CONDITION = {
    uid: createUid(),
    condition_number: "",
    description: "",
  };
  const [preConditions, setPreConditions] = useState([
    { ...EMPTY_PRE_CONDITION },
  ]);
  // Steps
  const EMPTY_STEPS = {
    uid: createUid(),
    step_number: "",
    expected_result: "",
    description: "",
  };
  const [testCaseSteps, setTestCaseSteps] = useState([{ ...EMPTY_STEPS }]);
  // Expected result
  const [expectedResult, setExpectedResult] = useState("");
  // Legal standard no.
  const [legalStandardOptionsList, setLegalStandardOptionsList] = useState([]);
  const [legalStandardsList, setLegalStandardsList] = useState([]);
  const [selectedLegalStandardId, setSelectedLegalStandardId] = useState(null);
  // Chapter
  const [chapterOptionsList, setChapterOptionsList] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  // Requirement no.
  const [requirementOptionsList, setRequirementOptionsList] = useState([]);
  const [selectedRequirementIds, setSelectedRequirementIds] = useState([]);

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  const [currentStepsSubTab, setCurrentStepsSubTab] = useState(0);

  // Reset to first tab when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentTab(0);
      setCurrentStepsSubTab(0);
    }
  }, [open]);

  // is add test case valid
  const isAddTestCaseValid = () => {
    return (
      testCaseId?.trim() !== "" &&
      !testCaseIdError &&
      testCaseName?.trim() !== "" &&
      !testCaseNameError &&
      description?.trim() !== "" &&
      !descriptionError &&
      status !== null &&
      status !== ""
    );
  };

  // Clear add test case fields
  const clearAddTestCaseFields = () => {
    // Test case id
    setTestCaseId("");
    setTestCaseIdError("");
    // Test case name
    setTestCaseName("");
    setTestCaseNameError("");
    // Description
    setDescription("");
    setDescriptionError("");
    setEnvironment(null);
    setIsDestructible(false);
    setNeedNewVehicle(false);
    // EE Component
    setSelectedEEComponentIds([]);
    // Software module
    setSelectedSoftwareModuleIds([]);
    // Vehicle system
    setSelectedVehicleSystemIds([]);
    // Vehicle component
    setSelectedVehicleComponentIds([]);
    // Selected component
    setSelectedComponent(COMPONENT.VS);
    // Status
    setStatus(null);
    // Pre-conditions
    setPreConditions([{ ...EMPTY_PRE_CONDITION }]);
    // Steps
    setTestCaseSteps([{ ...EMPTY_STEPS }]);
    // Expected result
    setExpectedResult("");
    // Legal standard no.
    setLegalStandardOptionsList([]);
    setLegalStandardsList([]);
    setSelectedLegalStandardId(null);
    // Chapter
    setChapterOptionsList([]);
    setSelectedChapter(null);
    // Requirement no.
    setRequirementOptionsList([]);
    setSelectedRequirementIds([]);
  };

  // get test environments
  const getTestEnvironments = async () => {
    let envResp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_environment,
        params: {
          page_number: 1,
          page_size: 10000,
        },
      }),
    );

    console.log("---envResp---", envResp);
    // let envList = envResp?.data?.map((env) => ({
    //   label: env?.name,
    //   value: env?.id,
    // }));
    const envList = Array.isArray(envResp?.data)
      ? envResp.data.map((env) => ({
          label: env.name,
          value: env.id,
        }))
      : [];
    setEnvironmentOptionsList(envList);
  };

  // Handle add test case
  const handleAddTestCase = async () => {
    // NOSONAR
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.add_test_case,
        params: {
          manual_test_case_id: testCaseId,
          name: testCaseName,
          description: description,
          test_environment: environment,
          is_destructable: isDestructible,
          need_new_vehicle: needNewVehicle,
          ...(selectedComponent === COMPONENT.EE &&
            selectedEEComponentIds?.length > 0 && {
              component_to_link: {
                entity_type: "ee",
                entity_ids: selectedEEComponentIds?.map((eeId) =>
                  Number(eeId?.value),
                ),
              },
            }),
          ...(selectedComponent === COMPONENT.SWM &&
            selectedSoftwareModuleIds?.length > 0 && {
              component_to_link: {
                entity_type: "swm",
                entity_ids: selectedSoftwareModuleIds?.map((swmId) =>
                  Number(swmId?.value),
                ),
              },
            }),
          ...(selectedComponent === COMPONENT.VS &&
            selectedVehicleSystemIds?.length > 0 && {
              component_to_link: {
                entity_type: "vs",
                entity_ids: selectedVehicleSystemIds?.map((vsId) =>
                  Number(vsId?.value),
                ),
              },
            }),
          ...(selectedComponent === COMPONENT.VC &&
            selectedVehicleComponentIds?.length > 0 && {
              component_to_link: {
                entity_type: "vc",
                entity_ids: selectedVehicleComponentIds?.map((vcId) =>
                  Number(vcId?.value),
                ),
              },
            }),
          ...(status && { status }),
          ...(preConditions?.length > 0 &&
            preConditions[0]?.description?.trim() && {
              preconditions: preConditions
                .filter((pc) => pc?.description?.trim())
                .map((pc, idx) => ({
                  condition_number: idx + 1,
                  description: pc.description,
                })),
            }),
          ...(testCaseSteps?.length > 0 &&
            testCaseSteps[0]?.description?.trim() && {
              steps: testCaseSteps
                .filter(
                  (step) =>
                    step?.description?.trim() && step?.expected_result?.trim(),
                )
                .map((step, idx) => ({
                  step_number: idx + 1,
                  description: step.description,
                  expected_result: step.expected_result,
                })),
            }),
          ...(expectedResult?.trim() && { expected_results: expectedResult }),
          ...(selectedRequirementIds?.length > 0 && {
            requirement_ids: selectedRequirementIds?.map((rId) =>
              Number(rId?.value || rId),
            ),
          }),
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      if (onSuccess) {
        onSuccess();
      }
      onClose(false);
      clearAddTestCaseFields();
    }
  };

  // Handle add pre-conditions fields
  const handleAddPreConditionsFields = () => {
    setPreConditions((prev) => [
      ...prev,
      { ...EMPTY_PRE_CONDITION, uid: createUid() },
    ]);
  };

  // Handle pre-conditions change
  const handlePreConditionsChange = (uid, field, value) => {
    setPreConditions((prev) =>
      prev.map((item) =>
        item.uid === uid ? { ...item, [field]: value } : item,
      ),
    );
  };

  // Handle add steps fields
  const handleAddTestCaseStepsFields = () => {
    setTestCaseSteps((prev) => [...prev, { ...EMPTY_STEPS, uid: createUid() }]);
  };

  // Handle steps change
  const handleTestCaseStepsChange = (uid, field, value) => {
    setTestCaseSteps((prev) =>
      prev.map((item) =>
        item.uid === uid ? { ...item, [field]: value } : item,
      ),
    );
  };

  const removeOptionByValue = (setState, value) => {
    setState((prev) => prev.filter((item) => item?.value !== value));
  };

  const removePreConditionByUid = (uid) => {
    setPreConditions((prev) => {
      const updated = prev.filter((pc) => pc.uid !== uid);
      return updated.length
        ? updated
        : [{ ...EMPTY_PRE_CONDITION, uid: createUid() }];
    });
  };

  const removeStepByUid = (uid) => {
    setTestCaseSteps((prev) => {
      const updated = prev.filter((step) => step.uid !== uid);
      return updated.length ? updated : [{ ...EMPTY_STEPS, uid: createUid() }];
    });
  };

  // Get ee components globally
  useEffect(() => {
    if (open) {
      const handleGetEEComponentsGlobally = async () => {
        const resp = await dispatch(
          HandleApiActions({
            ...testCasesActions.get_ee_components_globally,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        );
        if (resp.error) {
          setSelectedEEComponentIds([]);
        } else {
          resp?.data
            ?.filter((ee) => ee?.status !== false)
            ?.map((ee) => ({
              value: ee?.ee_id,
              label: ee?.description,
            }));
          setSelectedEEComponentIds([]);
        }
      };
      handleGetEEComponentsGlobally();
      getTestEnvironments();
    }
  }, [dispatch, open]);

  // Get legal standards
  useEffect(() => {
    const handleGetLegalStandards = async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_legal_standards,
          params: {
            page_number: 1,
            page_size: 10000,
          },
        }),
      );
      if (resp.error) {
        setLegalStandardsList([]);
        setLegalStandardOptionsList([]);
        setSelectedLegalStandardId(null);
      } else {
        setLegalStandardsList(resp?.data);
        const filteredLegalStandardOptionsList = resp?.data?.map((ls) => ({
          value: ls?.ls_id,
          label: ls?.standard_number,
        }));
        setLegalStandardOptionsList(filteredLegalStandardOptionsList);
        setSelectedLegalStandardId(null);
      }
    };

    if (open) {
      handleGetLegalStandards();
    }
  }, [dispatch, open]);

  // Set chapters
  useEffect(() => {
    const filteredSelectedLegalStandardDetails = legalStandardsList?.find(
      (ls) => ls?.ls_id === selectedLegalStandardId,
    );
    const filteredChaptersOptionsList =
      filteredSelectedLegalStandardDetails?.chapters?.map((ch) => ({
        value: ch,
        label: ch,
      }));
    setChapterOptionsList(filteredChaptersOptionsList);
    setSelectedChapter(null);
  }, [legalStandardsList, selectedLegalStandardId]);

  // Get legal requirements
  useEffect(() => {
    const handleGetLegalRequirements = async ({ legalStandardId, chapter }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_legal_requirements,
          params: {
            ls_id: legalStandardId,
            chapter: chapter,
            sort_column: "created_at",
            sort_order: "ASC",
            page_number: 1,
            page_size: 10000,
          },
        }),
      );
      if (resp.error) {
        setRequirementOptionsList([]);
      } else {
        const filteredRequirementOptionsList = resp?.data?.map((lr) => ({
          value: lr?.lr_id,
          label: lr?.req_number,
        }));
        setRequirementOptionsList(filteredRequirementOptionsList);
      }
    };

    if (selectedLegalStandardId && selectedChapter) {
      handleGetLegalRequirements({
        legalStandardId: selectedLegalStandardId,
        chapter: selectedChapter,
      });
    }
  }, [dispatch, legalStandardsList, selectedLegalStandardId, selectedChapter]);

  // Test case statuses
  useEffect(() => {
    const formatStatusLabel = (status) => {
      return status
        ?.split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const filteredTestCaseStatus = testCaseStatus?.map((tcs) => ({
      value: tcs,
      label: formatStatusLabel(tcs),
    }));
    setStatusOptionsList(filteredTestCaseStatus);
  }, [testCaseStatus, open]);

  // Check if DETAILS tab is valid
  const isDetailsTabValid = () => {
    return (
      testCaseId?.trim() !== "" &&
      testCaseName?.trim() !== "" &&
      description?.trim() !== "" &&
      environment !== null &&
      isDestructible !== null &&
      needNewVehicle !== null &&
      status !== null &&
      status !== ""
    );
  };

  // DETAILS TAB CONTENT
  const renderDetailsTab = () => (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: "1000px" }}>
      <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
        <Box sx={{ flex: 1 }}>
          <CustomFormTextFieldV2
            label="Test Case ID"
            required={true}
            value={testCaseId}
            setValue={setTestCaseId}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                height: 32,
                borderRadius: 0,
                // "& fieldset": {
                //   borderColor: "#000000",
                //   borderWidth: "1px",
                // },
                "&:hover fieldset": {
                  borderColor: "#000000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <CustomSelectV2
            required={true}
            size={"small"}
            label={"Status"}
            variant={"outlined"}
            optionsList={statusOptionsList}
            selectedValue={status}
            handleOnChange={(e) => setStatus(e.target.value)}
            // sltFldStyle={{ width: "100%" }}
          />
        </Box>
      </Box>

      <CustomFormTextFieldV2
        label="Test Case Name"
        required={true}
        value={testCaseName}
        setValue={setTestCaseName}
        txtFldStyle={{
          width: "100%",
          "& .MuiOutlinedInput-root": { height: 32 },
          "& .MuiOutlinedInput-root fieldset": { borderRadius: 0 },
          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
        }}
        // placeholder="Enter Test Case Name"
      />

      <Box>
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            ...componentStyle.font.form.textFieldLabel,
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          Description <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          // placeholder="Enter expected result"
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              // "& fieldset": {
              //   borderColor: "#000000",
              //   borderWidth: "1px",
              // },
              "&:hover fieldset": {
                borderColor: "#000000",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#000000",
              },
            },
          }}
        />
      </Box>

      <CustomSelectV2
        label={"Environment"}
        selectedValue={environment}
        handleOnChange={(e) => {
          setEnvironment(e.target.value);
        }}
        optionsList={environmentOptionsList}
        required={true}
      />

      <CustomRadioButtonV2
        value={String(isDestructible)}
        onChange={(e) => {
          const nextValue = e.target.value === "true";
          setIsDestructible(nextValue);
          if (!nextValue) setNeedNewVehicle(false);
        }}
        data={[
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ]}
        displayDirection="row"
        label={"Destructible"}
        required={true}
      />

      {isDestructible && (
        <CustomCheckBoxV2
          value={needNewVehicle}
          onChange={(e) => setNeedNewVehicle(e.target.checked)}
          label="Need New Vehicle"
        />
      )}
    </Stack>
  );

  // LINK TO TAB CONTENT
  const renderLinkToTab = () => (
    // NOSONAR
    <Stack spacing={3} sx={{ width: "100%", maxWidth: "1000px", p: 2 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          Type
        </Typography>
        <Box sx={{ ml: "10px" }}>
          <CustomRadioButtonV2
            value={selectedComponent}
            onChange={(e) => setSelectedComponent(e.target.value)}
            data={[
              { value: COMPONENT.VS, label: "Vehicle System" },
              { value: COMPONENT.VC, label: "Vehicle Component" },
              { value: COMPONENT.EE, label: "EE Component" },
              { value: COMPONENT.SWM, label: "Software Module" },
            ]}
            displayDirection="row"
          />
        </Box>
      </Box>

      {selectedComponent === COMPONENT.EE && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select Component
            </Typography>
          </Box>
          <CustomMultiSelectAutocompleteWithApiV2
            apiAction={testCasesActions.get_ee_components_globally}
            params={{ page_number: 1, page_size: 10000 }}
            searchKey={"description"}
            mapResponseToOptions={(data) =>
              (data || [])
                .filter((ee) => ee?.status !== false)
                .map((ee) => ({ value: ee?.ee_id, label: ee?.description }))
            }
            selectedValues={selectedEEComponentIds}
            setSelectedValues={setSelectedEEComponentIds}
            size="small"
            variant="outlined"
            placeholder="Search Description"
            paperSx={{}}
            skipInitialSearch={false}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                // height: 32,
                borderRadius: 0,
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": { borderColor: "#000000" },
              },
              // "& .MuiOutlinedInput-input": {
              //   padding: "6px 10px",
              // },
            }}
            enableSelectSimilar={true}
            enableSelectAll={true}
          />

          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedEEComponentIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedEEComponentIds?.map((eeComponent) => (
              <Box
                key={eeComponent?.value}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() => {
                  removeOptionByValue(
                    setSelectedEEComponentIds,
                    eeComponent?.value,
                  );
                }}
              >
                {eeComponent?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {selectedComponent === COMPONENT.SWM && (
        <Box>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: "14px", color: "#666" }}>
                Search / Select Module
              </Typography>
            </Box>

            <CustomMultiSelectAutocompleteWithApiV2
              apiAction={testCasesActions.get_swm_globally}
              params={{ page_number: 1, page_size: 10000 }}
              searchKey={"description"}
              mapResponseToOptions={(data) =>
                (data || [])
                  .filter((swm) => swm?.status !== false)
                  .map((swm) => ({
                    value: swm?.swm_id,
                    label: swm?.description,
                  }))
              }
              selectedValues={selectedSoftwareModuleIds}
              setSelectedValues={setSelectedSoftwareModuleIds}
              size="small"
              variant="outlined"
              placeholder="Search Description"
              paperSx={{}}
              skipInitialSearch={false}
              txtFldStyle={{
                "& .MuiOutlinedInput-root": {
                  // height: 32,
                  borderRadius: 0,
                  "&:hover fieldset": { borderColor: "#000000" },
                  "&.Mui-focused fieldset": { borderColor: "#000000" },
                },
              }}
              enableSelectSimilar={true}
              enableSelectAll={true}
            />

            <Box
              sx={{
                maxHeight: "95px",
                width: "100%",
                overflow: "auto",
                px: 0.5,
                pt: 0.5,
                mb: 2,
                ...(selectedSoftwareModuleIds?.length > 0 && {
                  border: "1px solid lightgrey",
                }),
              }}
            >
              {selectedSoftwareModuleIds?.map((softwareModule) => (
                <Box
                  key={softwareModule?.value}
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    mb: 0.5,
                    mr: 0.5,
                    backgroundColor: "#232323",
                    color: "white",
                    borderRadius: 0,
                    padding: "4px 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                    },
                  }}
                  onClick={() => {
                    removeOptionByValue(
                      setSelectedSoftwareModuleIds,
                      softwareModule?.value,
                    );
                  }}
                >
                  {softwareModule?.label}
                  <span
                    style={{
                      marginLeft: "8px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </span>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {selectedComponent === COMPONENT.VS && (
        // <MultiSelectTextFieldNTC
        //   size="small"
        //   label="SEARCH /SELECT COMPONENT"
        //   optionsList={vehicleSystemsOptionsList}
        //   selectedValues={selectedVehicleSystemIds}
        //   setSelectedValues={setSelectedVehicleSystemIds}
        //   sltFldStyle={{ width: "100%" }}
        // />
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select System
            </Typography>
          </Box>

          <CustomMultiSelectAutocompleteWithApiV2
            apiAction={testCasesActions.get_vs_globally}
            params={{ page_number: 1, page_size: 10000 }}
            searchKey={"assembly_number"}
            mapResponseToOptions={(data) =>
              (data || [])
                .filter((vs) => vs?.status !== false)
                .map((vs) => ({
                  value: vs?.id,
                  label: vs?.assembly_number,
                }))
            }
            selectedValues={selectedVehicleSystemIds}
            setSelectedValues={setSelectedVehicleSystemIds}
            size="small"
            variant="outlined"
            placeholder="Search Assembly Number"
            paperSx={{}}
            skipInitialSearch={false}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                // height: 32,
                borderRadius: 0,
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": { borderColor: "#000000" },
              },
            }}
            enableSelectSimilar={true}
            enableSelectAll={true}
          />

          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedVehicleSystemIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedVehicleSystemIds?.map((vehicleSystem) => (
              <Box
                key={vehicleSystem?.value}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() => {
                  removeOptionByValue(
                    setSelectedVehicleSystemIds,
                    vehicleSystem?.value,
                  );
                }}
              >
                {vehicleSystem?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {selectedComponent === COMPONENT.VC && (
        <Box>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: "14px", color: "#666" }}>
                Search / Select Component
              </Typography>
            </Box>

            <CustomMultiSelectAutocompleteWithApiV2
              apiAction={testCasesActions.get_vc_globally}
              params={{ page_number: 1, page_size: 10000 }}
              searchKey={"component_number"}
              mapResponseToOptions={(data) =>
                (data || [])
                  .filter((vc) => vc?.status !== false)
                  .map((vc) => ({
                    value: vc?.id,
                    label: vc?.component_number,
                  }))
              }
              selectedValues={selectedVehicleComponentIds}
              setSelectedValues={setSelectedVehicleComponentIds}
              size="small"
              variant="outlined"
              placeholder="Search Component Number"
              paperSx={{}}
              skipInitialSearch={false}
              txtFldStyle={{
                "& .MuiOutlinedInput-root": {
                  // height: 32,
                  borderRadius: 0,
                  "&:hover fieldset": { borderColor: "#000000" },
                  "&.Mui-focused fieldset": { borderColor: "#000000" },
                },
              }}
              enableSelectSimilar={true}
              enableSelectAll={true}
            />

            <Box
              sx={{
                maxHeight: "95px",
                width: "100%",
                overflow: "auto",
                px: 0.5,
                pt: 0.5,
                mb: 2,
                ...(selectedVehicleComponentIds?.length > 0 && {
                  border: "1px solid lightgrey",
                }),
              }}
            >
              {selectedVehicleComponentIds?.map((vehicleComponent) => (
                <Box
                  key={vehicleComponent?.value}
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    mb: 0.5,
                    mr: 0.5,
                    backgroundColor: "#232323",
                    color: "white",
                    borderRadius: 0,
                    padding: "4px 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                    },
                  }}
                  onClick={() => {
                    removeOptionByValue(
                      setSelectedVehicleComponentIds,
                      vehicleComponent?.value,
                    );
                  }}
                >
                  {vehicleComponent?.label}
                  <span
                    style={{
                      marginLeft: "8px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </span>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <CustomSelectV2
        size={"small"}
        label={"Select Legal Standard No"}
        // variant={"standard"}
        optionsList={legalStandardOptionsList}
        selectedValue={selectedLegalStandardId}
        handleOnChange={(e) => setSelectedLegalStandardId(e.target.value)}
        // sltFldStyle={styles.selectTxtStyle}
        required={false}
        placeholder={false}
      />
      <CustomSelectV2
        size={"small"}
        label={"Select Chapter"}
        // variant={"standard"}
        optionsList={chapterOptionsList}
        selectedValue={selectedChapter}
        handleOnChange={(e) => setSelectedChapter(e.target.value)}
        // sltFldStyle={styles.selectTxtStyle}
        required={false}
        placeholder={false}
      />

      <Box>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            Search / Select Requirement No
          </Typography>
        </Box>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={requirementOptionsList}
          value={selectedRequirementIds}
          onChange={(event, newValue) => {
            setSelectedRequirementIds(newValue);
          }}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          renderOption={(props, option, { selected }) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                p: "8px 12px !important",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <Checkbox
                checked={selected}
                sx={{
                  marginRight: 1,
                  color: "#000",
                  "&.Mui-checked": {
                    color: "#000",
                  },
                }}
              />
              {option?.label}
            </Box>
          )}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              // placeholder="Select countries..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  borderRadius: 0,
                  // "& fieldset": {
                  //   borderColor: "#000000",
                  //   borderWidth: "1px",
                  // },
                  "&:hover fieldset": {
                    borderColor: "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                  },
                },
              }}
            />
          )}
          slotProps={{
            paper: {
              sx: {
                maxHeight: 250,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#555",
                  },
                },
              },
            },
          }}
        />

        <Box
          sx={{
            maxHeight: "95px",
            width: "100%",
            overflow: "auto",
            px: 0.5,
            pt: 0.5,
            mb: 2,
            ...(selectedRequirementIds?.length > 0 && {
              border: "1px solid lightgrey",
            }),
          }}
        >
          {selectedRequirementIds?.map((requirement) => (
            <Box
              key={requirement?.value}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mb: 0.5,
                mr: 0.5,
                backgroundColor: "#232323",
                color: "white",
                borderRadius: 0,
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#1a1a1a",
                },
              }}
              onClick={() => {
                removeOptionByValue(
                  setSelectedRequirementIds,
                  requirement?.value,
                );
              }}
            >
              {requirement?.label}
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ×
              </span>
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );

  // STEPS TAB CONTENT
  const renderStepsTab = () => (
    <Stack spacing={0} sx={{ width: "100%", maxWidth: "1000px" }}>
      <CustomTabsV2
        value={currentStepsSubTab}
        disableTabClick={false}
        centerTabs={false}
        onTabChange={(newTab) => setCurrentStepsSubTab(newTab)}
        tabs={[
          {
            name: "Pre Condition",
            count: preConditions?.length || 0,
            content: renderPreConditionsTab(),
          },
          {
            name: "Steps",
            count: testCaseSteps?.length || 0,
            content: renderStepsContentTab(),
          },
          {
            name: "Expected Result",
            count: "",
            content: renderExpectedResultTab(),
          },
        ]}
      />
    </Stack>
  );

  // PRE-CONDITIONS SUB-TAB CONTENT
  const renderPreConditionsTab = () => (
    // NOSONAR
    <Box>
      {preConditions?.map((item, index) => (
        <Box
          key={item.uid}
          sx={{
            // mb: 2,
            // p: 2,
            bgcolor: "#f9f9f9",
            borderRadius: "4px",
            position: "relative",
          }}
        >
          <IconButton
            size="small"
            sx={{ position: "absolute", right: 2, top: 2 }}
            onClick={() => removePreConditionByUid(item.uid)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ fontSize: "14px" }}>
            {index + 1}.Pre Condition
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={item?.description}
            onChange={(e) =>
              handlePreConditionsChange(item.uid, "description", e.target.value)
            }
            // placeholder="Enter pre-condition"
            variant="outlined"
            size="small"
            sx={{
              mt: 0.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                // "& fieldset": {
                //   borderColor: "#000000",
                //   borderWidth: "1px",
                // },
                "&:hover fieldset": {
                  borderColor: "#000000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
        </Box>
      ))}
      <Box>
        <IconButton
          size="small"
          onClick={handleAddPreConditionsFields}
          sx={{ mt: 0.5 }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Box>
  );

  // STEPS CONTENT SUB-TAB
  const renderStepsContentTab = () => (
    // NOSONAR
    <Box>
      {testCaseSteps?.map((item, index) => (
        <Box
          key={item.uid}
          sx={{
            // mb: 2,
            // p: 2,
            bgcolor: "#f9f9f9",
            borderRadius: "4px",
            position: "relative",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            sx={{ position: "absolute", right: 2, top: 2 }}
            onClick={() => removeStepByUid(item.uid)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="caption" sx={{ fontSize: "14px" }}>
              {index + 1}.Step
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={item?.description}
              onChange={(e) =>
                handleTestCaseStepsChange(
                  item.uid,
                  "description",
                  e.target.value,
                )
              }
              // placeholder="Enter step description"
              variant="outlined"
              size="small"
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  // "& fieldset": {
                  //   borderColor: "#000000",
                  //   borderWidth: "1px",
                  // },
                  "&:hover fieldset": {
                    borderColor: "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                  },
                },
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: "14px" }}>
              {index + 1}.Expected Result
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={item?.expected_result}
              onChange={(e) =>
                handleTestCaseStepsChange(
                  item.uid,
                  "expected_result",
                  e.target.value,
                )
              }
              // placeholder="Enter expected result"
              variant="outlined"
              size="small"
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  // "& fieldset": {
                  //   borderColor: "#000000",
                  //   borderWidth: "1px",
                  // },
                  "&:hover fieldset": {
                    borderColor: "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                  },
                },
              }}
            />
          </Box>
        </Box>
      ))}
      <IconButton
        size="small"
        onClick={handleAddTestCaseStepsFields}
        sx={{ mt: 0.5 }}
      >
        <AddIcon />
      </IconButton>
    </Box>
  );

  // EXPECTED RESULT SUB-TAB CONTENT
  const renderExpectedResultTab = () => (
    <Box>
      <Typography sx={{ mb: 1 }}>Expected Result</Typography>
      <TextField
        fullWidth
        multiline
        rows={3}
        value={expectedResult}
        onChange={(e) => setExpectedResult(e.target.value)}
        // placeholder="Enter expected result"
        variant="outlined"
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
            // "& fieldset": {
            //   borderColor: "#000000",
            //   borderWidth: "1px",
            // },
            "&:hover fieldset": {
              borderColor: "#000000",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#000000",
            },
          },
        }}
      />
    </Box>
  );

  let isDialogActionDisabled = false;
  if (currentTab === 0) {
    isDialogActionDisabled = !isDetailsTabValid();
  } else if (currentTab === 2) {
    isDialogActionDisabled = !isAddTestCaseValid();
  }

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={() => {
        onClose(false);
        clearAddTestCaseFields();
        setCurrentTab(0);
      }}
      title="Add Test Case"
      actionButtonStyle={currentTab === 0 ? "continuous" : "spaced"}
      successButtonName={currentTab === 2 ? "Create" : "Save & Next"}
      rejectButtonName={currentTab === 0 ? "Cancel" : "Back"}
      onClickSuccess={() => {
        if (currentTab === 2) {
          handleAddTestCase();
        } else {
          setCurrentTab(currentTab + 1);
        }
      }}
      onClickReject={() => {
        if (currentTab === 0) {
          onClose(false);
          clearAddTestCaseFields();
          setCurrentTab(0);
        } else {
          setCurrentTab(currentTab - 1);
        }
      }}
      onDisabled={isDialogActionDisabled}
      paperSx={{ width: "1000px", maxWidth: "95vw" }}
      contentSx={{ p: 0 }}
    >
      <CustomTabsV2
        value={currentTab}
        disableTabClick={true}
        centerTabs={true}
        onTabChange={(newTab) => setCurrentTab(newTab)}
        tabs={[
          {
            name: "Details",
            count: "",
            content: renderDetailsTab(),
          },
          {
            name: "Link To",
            count: "",
            content: renderLinkToTab(),
          },
          {
            name: "Steps",
            count: "",
            content: renderStepsTab(),
          },
        ]}
      />
    </CustomDialogBoxV2>
  );
}

export default AddTestCaseDialog;

AddTestCaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
